import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import { readableNumber } from '@attain-apps/string-utils'

/** Number component displaying different formats of numbers  */
@ngCore.Component({
  selector: 'atn-value-number',
  standalone: true,
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component implements ngCore.OnDestroy {
  /**
   * Div that displays either {@link readableNumberSg} or
   * overflow placeholders (#) based on the {@link isOverflowingSg}
   */
  public readonly numberElSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLDivElement>>('number')
  /**
   * Div that is used for calculating overflow
   */
  public readonly hiddenElSg =
    ngCore.viewChild<ngCore.ElementRef<HTMLDivElement>>('hidden')
  /**
   * Scroll width of the number div, which is visible content width + hidden
   * overflow content
   */
  private readonly _hiddenElScrollWidthSg = ngCore.signal<number | undefined>(
    undefined,
  )
  /**
   * Client width of the number div, which is width of the visible content
   */
  private readonly _numberElClientWidthSg = ngCore.signal<number | undefined>(
    undefined,
  )
  /**
   * Observer observing window resize
   */
  private readonly _resizeObserver = new ResizeObserver(() => {
    const numberEl = this.numberElSg()?.nativeElement
    const hiddenEl = this.hiddenElSg()?.nativeElement
    if (numberEl) {
      this._numberElClientWidthSg.set(numberEl.clientWidth)
    }

    if (hiddenEl) {
      this._hiddenElScrollWidthSg.set(hiddenEl.scrollWidth)
    }
  })

  /**
   * Value to transform into the {@link readableNumberSg} which is to be
   * displayed on the UI
   */
  public readonly valueSg = ngCore.input<
    ents.core.common.value.Raw | undefined
  >(undefined, {
    alias: 'value',
  })

  /** Value info needed to calculate the {@link readableNumberSg} */
  public readonly valueInfoSg = ngCore.input<
    ents.core.common.value.Info | undefined
  >(undefined, {
    alias: 'valueInfo',
  })

  /**
   * Signal used to switch between {@link readableNumberSg} and
   * overflow placeholders (#)
   */
  public readonly isOverflowingSg = ngCore.computed(
    (
      hiddenDivScrollWidth = this._hiddenElScrollWidthSg(),
      numberDivClientWidth = this._numberElClientWidthSg(),
    ) => (hiddenDivScrollWidth ?? 0) > (numberDivClientWidth ?? 0),
  )

  /**
   * Formatted value to display in the template
   */
  public readonly readableNumberSg = ngCore.computed(
    (value = this.valueSg(), valueInfo = this.valueInfoSg()) => {
      // Nothing we can do at this point, just return undefined
      if (value === undefined || typeof value !== 'number') {
        return undefined
      }

      const valueType =
        valueInfo?.type ??
        ents.core.common.value.types.Intrinsic.inferFrom(value)

      // If we we're able to determine a type, we can use to get the
      // readableNumber preset
      const preset =
        ents.core.common.value.types.Intrinsic.ForNumber.isForNumber(valueType)
          ? ents.core.common.value.types.Intrinsic.ForNumber.toReadableNumberPreset(
              valueType,
            )
          : undefined

      // If we don't have preset, we at least want to display something
      const _readableNumber = readableNumber(
        value,
        preset ?? { decimals: valueInfo?.precision },
      )

      return _readableNumber
    },
  )
  /**
   * Overflow placeholder, substituting {@link readableNumberSg readableNumber}
   * width array of # characters
   */
  public readonly overflowPlaceholderSg = ngCore.computed(
    (_readableNumber = this.readableNumberSg()) =>
      _readableNumber?.replace(/./g, '#'),
  )

  constructor() {
    /**
     * Observe numberEl for resize as soon as it gets created
     */
    ngCore.effect(() => {
      const numberEl = this.numberElSg()?.nativeElement
      if (numberEl) {
        this._resizeObserver.observe(numberEl)
      }
    })
  }

  /**
   * Disconnecting observer upon destroying the component
   */
  ngOnDestroy(): void {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
    }
  }
}
